import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IAdvanceCustomStatus } from "store/advanceCustomStatusStore";
import { createAdvanceCustomStatus, updateAdvanceCustomStatus } from "store/advanceCustomStatusStore";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

type AdvanceCustomStatusFormValues = {
    name: string;
    customIdentifier: string;
};

type AdvanceCustomStatusFormProps = {
    advanceStatus?: IAdvanceCustomStatus;
    onClose: () => void;
    onRequestDelete?: () => void;
};

const AdvanceCustomStatusForm = ({ advanceStatus, onClose, onRequestDelete }: AdvanceCustomStatusFormProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isEdit = advanceStatus != null;

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<AdvanceCustomStatusFormValues>({
        defaultValues: {
            name: advanceStatus?.name ?? "",
            customIdentifier: advanceStatus?.customIdentifier ?? "",
        },
    });

    const onSubmit = async ({ name, customIdentifier }: AdvanceCustomStatusFormValues) => {
        const trimmedName = name.trim();
        const trimmedIdentifier = customIdentifier.trim();
        if (!trimmedName) return;

        const payload = {
            name: trimmedName,
            customIdentifier: trimmedIdentifier || null,
        };

        const response = isEdit
            ? await dispatch(updateAdvanceCustomStatus({ id: advanceStatus.id, ...payload }))
            : await dispatch(createAdvanceCustomStatus(payload));

        if (response.meta.requestStatus === "fulfilled") {
            onClose();
        }
    };

    return (
        <form className={styles["advance-status-form"]} onSubmit={handleSubmit(onSubmit)}>
            <TextFormComponent
                label={t("Settings.AdvanceStatusName")}
                placeholder={t("Settings.AdvanceStatusPlaceholder")}
                control={control}
                controlName="name"
                required
                fullWidth
                error={errors.name}
            />
            <TextFormComponent
                label={t("Settings.AdvanceStatusIdentifier")}
                placeholder={t("Settings.AdvanceStatusIdentifierPlaceholder")}
                control={control}
                controlName="customIdentifier"
                fullWidth
                error={errors.customIdentifier}
            />
            <div className={styles["advance-status-form-actions"]}>
                {isEdit && onRequestDelete ? (
                    <StyledButton buttonType="danger" type="button" onClick={onRequestDelete} fullWidth>
                        {t("Settings.DeleteAdvanceStatus")}
                    </StyledButton>
                ) : (
                    <StyledButton buttonType="secondary" type="button" onClick={onClose} fullWidth>
                        {t("Settings.AdvanceStatusCancel")}
                    </StyledButton>
                )}
                <StyledButton buttonType="primary" type="submit" loading={isSubmitting} fullWidth>
                    {t("Settings.SaveAdvanceStatus")}
                </StyledButton>
            </div>
        </form>
    );
};

export default AdvanceCustomStatusForm;
